<template>
    <!-- Area > Zone selector with search, expand/collapse and tri-state area
         checkboxes -- the zone-level sibling of PointSelectTree. Room Condition
         selects whole zones, so the tree stops at zones (no points beneath).

         Data-source agnostic: it takes an Area > Zone tree and emits the selected
         zone ids. -->
    <div class="overflow-clip rounded-md border border-gray-200 bg-white">
        <div class="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
            <input  v-model="searchQuery"
                    type="text"
                    :placeholder="searchPlaceholder"
                    class="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm transition-colors focus:border-blue-500 focus:ring-blue-500"
            >
            <button type="button"
                    :disabled="allSelected || visibleZoneIds.length === 0"
                    class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
                    @click="selectAllVisible"
            >
                {{ searchQuery.trim() ? 'Select all matching' : 'Select all' }}
            </button>
            <button type="button"
                    class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                    @click="toggleAllExpanded"
            >
                {{ allExpanded ? 'Collapse all' : 'Expand all' }}
            </button>
        </div>

        <div    class="flex h-12 items-center justify-between gap-3 border-b border-gray-100 px-4 transition-colors"
                :class="selectedCount > 0 ? 'bg-blue-50' : 'bg-gray-50'"
        >
            <span   class="text-sm font-medium"
                    :class="selectedCount > 0 ? 'text-blue-900' : 'text-gray-500'">
                {{ selectedCount > 0
                    ? `${selectedCount} ${selectedCount === 1 ? 'zone' : 'zones'} selected`
                    : 'No zones selected' }}
            </span>

            <button v-if="selectedCount > 0"
                    type="button"
                    class="inline-flex items-center gap-1 text-sm font-medium text-blue-700 transition-colors hover:text-blue-900"
                    @click="clearSelection"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                Clear
            </button>
        </div>

        <div    v-if="filteredTree.length === 0"
                class="flex min-h-64 items-center justify-center p-8">
            <div class="flex flex-col items-center justify-center space-y-0.5">
                <span class="lnr icon-inbox !text-4xl !text-gray-500 mb-2 inline-block w-8 text-center"></span>
                <span class="text-base font-medium">
                    {{ searchQuery.trim() ? 'No matching zones' : 'No zones available' }}
                </span>
                <span class="text-sm text-gray-500">
                    {{ searchQuery.trim() ? 'Try a different search term.' : 'No zones are accessible for this report.' }}
                </span>
            </div>
        </div>

        <ul v-else
            class="m-0 max-h-96 list-none overflow-y-auto p-0 divide-y divide-gray-100">
            <template   v-for="(area, areaIdx) in filteredTree"
                        :key="`a-${area.id}`">
                <li :class="areaIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
                    <div    class="flex cursor-pointer select-none items-center gap-2 py-3 pl-2 pr-4 font-medium text-gray-900 hover:bg-gray-100 sm:px-4"
                            role="button"
                            :aria-expanded="isExpanded(area.id)"
                            @click="toggleExpanded(area.id)"
                    >
                        <span class="text-gray-400">
                            <svg v-if="isExpanded(area.id)" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M6 9l6 6l6 -6" /></svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 6l6 6l-6 6" /></svg>
                        </span>
                        <label  class="m-0 inline-flex cursor-pointer items-center gap-2"
                                @click.stop>
                            <input  type="checkbox"
                                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    :checked="areaState(area) === 'all'"
                                    :indeterminate.prop="areaState(area) === 'some'"
                                    @change="toggleArea(area, $event.target.checked)"
                            >
                            {{ area.name }}
                            <span class="text-xs font-normal text-gray-500">({{ area.zones.length }})</span>
                        </label>
                    </div>
                </li>

                <template v-if="isExpanded(area.id)">
                    <li v-for="zone in area.zones"
                        :key="`z-${zone.id}`"
                        class="bg-white"
                    >
                        <label class="m-0 flex cursor-pointer items-center gap-2 py-2.5 pl-8 pr-4 text-sm text-gray-700 hover:bg-gray-100 sm:pl-16">
                            <input  type="checkbox"
                                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    :checked="isSelected(zone.id)"
                                    @change="toggleZone(zone.id, $event.target.checked)"
                            >
                            {{ zone.name }}
                        </label>
                    </li>
                </template>
            </template>
        </ul>
    </div>
</template>

<script>
import _ from "lodash"

export default {
    name: "ZoneSelectTree",
    emits: ['update:modelValue'],
    props: {
        // Selected zone ids.
        modelValue: {
            type: Array,
            default: () => ([]),
        },
        // [{ id, name, zones: [{ id, name }] }]
        tree: {
            type: Array,
            default: () => ([]),
        },
        searchPlaceholder: {
            type: String,
            default: 'Search areas or zones',
        },
        expandedByDefault: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            searchQuery: '',
            collapsed: {},
        }
    },
    computed: {
        selected() {
            return this.modelValue ?? []
        },
        selectedCount() {
            return this.selected.length
        },
        filteredTree() {
            const query = this.searchQuery.trim().toLowerCase()
            if (query === '') {
                return this.tree
            }

            const matches = (value) => (value ?? '').toLowerCase().indexOf(query) !== -1

            return this.tree
                .map((area) => {
                    if (matches(area.name)) {
                        return area
                    }
                    const zones = area.zones.filter((zone) => matches(zone.name))
                    return zones.length > 0 ? {...area, zones} : null
                })
                .filter(Boolean)
        },
        visibleZoneIds() {
            return this.filteredTree.flatMap((area) => area.zones.map((zone) => zone.id))
        },
        allSelected() {
            return this.visibleZoneIds.length > 0
                && this.visibleZoneIds.every((id) => this.isSelected(id))
        },
        allExpanded() {
            return this.filteredTree.every((area) => this.isExpanded(area.id))
        },
    },
    methods: {
        isExpanded(id) {
            const explicit = this.collapsed[id]
            return explicit === undefined ? this.expandedByDefault : explicit
        },
        toggleExpanded(id) {
            this.collapsed = {...this.collapsed, [id]: ! this.isExpanded(id)}
        },
        toggleAllExpanded() {
            const expand = ! this.allExpanded
            const areas = {}
            this.tree.forEach((area) => {
                areas[area.id] = expand
            })
            this.collapsed = areas
        },
        isSelected(zoneId) {
            return this.selected.indexOf(zoneId) !== -1
        },
        areaState(area) {
            const ids = area.zones.map((zone) => zone.id)
            if (ids.length === 0) {
                return 'none'
            }
            const count = ids.filter((id) => this.isSelected(id)).length
            if (count === 0) {
                return 'none'
            }
            return count === ids.length ? 'all' : 'some'
        },
        emitSelection(ids) {
            this.$emit('update:modelValue', _.uniq(ids))
        },
        setSelected(ids, selected) {
            this.emitSelection(selected
                ? [...this.selected, ...ids]
                : this.selected.filter((id) => ids.indexOf(id) === -1))
        },
        toggleZone(zoneId, selected) {
            this.setSelected([zoneId], selected)
        },
        toggleArea(area, selected) {
            this.setSelected(area.zones.map((zone) => zone.id), selected)
        },
        selectAllVisible() {
            this.setSelected(this.visibleZoneIds, true)
        },
        clearSelection() {
            this.emitSelection([])
        },
    },
}
</script>
